// Client login: email + password for store (cotizaciones). Redirects to /store after sign-in.
// Reset de contraseña propio: enlace con ?reset=TOKEN enviado por nuestro Worker (mismo template Padmora).

import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { postWorkerApi } from '@/lib/worker-api';
import { reportCaughtError } from '@/lib/report-error';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type View = 'login' | 'forgot' | 'new-password';

const StoreLogin = () => {
  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  /** Token from ?reset= (custom reset flow); when set, new-password submit calls worker confirm API. */
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const from =
    (location.state as { from?: string } | null)?.from ??
    (new URLSearchParams(location.search).get('redirect')?.trim() || undefined);

  // Custom reset: detect ?reset=TOKEN and show new-password form (link sent by our Worker).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('reset');
    if (token?.trim()) {
      setResetToken(token.trim());
      setView('new-password');
      // Clean URL from token so refresh doesn't resubmit
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (view === 'login') {
      if (!email?.trim() || !password) return;
      setLoading(true);
      try {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password,
        });
        if (error) throw error;
        toast({ title: 'Sesión iniciada', description: 'Redirigiendo...' });
        await supabase.auth.getSession();
        queryClient.invalidateQueries({ queryKey: ['store', 'client', 'lead'] });

        // Determinar destino: from/redirect explícito > pendingReclaimToken en localStorage > /store
        let target = from && from.startsWith('/store') ? from : null;
        if (!target) {
          const pendingToken = localStorage.getItem('pendingReclaimToken');
          if (pendingToken) target = `/store/catalog/${pendingToken}/reclaim`;
        }
        if (!target) target = '/store';

        // Carga completa para que la sesión se establezca limpiamente en la app.
        window.location.href = target;
      } catch (e) {
        reportCaughtError(e, { screen: 'store_login', action: 'sign_in' });
        toast({
          title: 'Error',
          description: e instanceof Error ? e.message : 'Email o contraseña incorrectos',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (view === 'forgot') {
      if (!email?.trim()) return;
      setLoading(true);
      const result = await postWorkerApi<{ success?: boolean }>('/api/request-password-reset', {
        email: email.trim().toLowerCase(),
      });
      setLoading(false);
      if (result.ok) {
        toast({
          title: 'Correo enviado',
          description: 'Si existe una cuenta con ese email, recibirás un enlace para restablecer tu contraseña (revisa también spam).',
        });
        setView('login');
      } else {
        reportCaughtError(new Error(result.error ?? 'request-password-reset failed'), {
          screen: 'store_login',
          action: 'reset_password',
        });
        toast({
          title: 'Error',
          description: result.error ?? 'No se pudo enviar el enlace',
          variant: 'destructive',
        });
      }
      return;
    }

    if (view === 'new-password') {
      if (!newPassword || newPassword.length < 8) {
        toast({ title: 'La contraseña debe tener al menos 8 caracteres', variant: 'destructive' });
        return;
      }
      if (newPassword !== confirmPassword) {
        toast({ title: 'Las contraseñas no coinciden', variant: 'destructive' });
        return;
      }
      if (!resetToken) {
        toast({
          title: 'Enlace inválido',
          description: 'Usa el enlace que te enviamos por correo para restablecer la contraseña.',
          variant: 'destructive',
        });
        return;
      }
      setLoading(true);
      const result = await postWorkerApi<{ success?: boolean }>('/api/confirm-password-reset', {
        token: resetToken,
        password: newPassword,
      });
      setLoading(false);
      if (result.ok) {
        toast({ title: 'Contraseña actualizada', description: 'Ya puedes iniciar sesión con tu nueva contraseña.' });
        setResetToken(null);
        setView('login');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        reportCaughtError(new Error(result.error ?? 'confirm-password-reset failed'), {
          screen: 'store_login',
          action: 'update_password',
        });
        toast({
          title: 'Error',
          description: result.error ?? 'No se pudo actualizar la contraseña',
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>
            {view === 'login' && 'Iniciar sesión'}
            {view === 'forgot' && 'Recuperar contraseña'}
            {view === 'new-password' && 'Nueva contraseña'}
          </CardTitle>
          <CardDescription>
            {view === 'login' && 'Accede con tu email y contraseña para ver el estado de tus cotizaciones.'}
            {view === 'forgot' && 'Indica tu email y te enviaremos un enlace para restablecer tu contraseña.'}
            {view === 'new-password' && 'Elige una nueva contraseña (mínimo 8 caracteres).'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {(view === 'login' || view === 'forgot') && (
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="mt-1"
                  autoComplete="email"
                />
              </div>
            )}

            {view === 'login' && (
              <div>
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                  autoComplete="current-password"
                />
                <p className="mt-1.5 text-right">
                  <button
                    type="button"
                    className="text-sm text-primary underline hover:no-underline"
                    onClick={() => setView('forgot')}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </p>
              </div>
            )}

            {view === 'new-password' && (
              <>
                <div>
                  <Label htmlFor="new-password">Nueva contraseña</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    className="mt-1"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirmar contraseña</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repite la contraseña"
                    className="mt-1"
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {view === 'login' && (loading ? 'Entrando...' : 'Entrar')}
              {view === 'forgot' && (loading ? 'Enviando...' : 'Enviar enlace de recuperación')}
              {view === 'new-password' && (loading ? 'Guardando...' : 'Guardar contraseña')}
            </Button>

            {view === 'forgot' && (
              <p className="text-center text-sm">
                <button
                  type="button"
                  className="text-primary underline hover:no-underline"
                  onClick={() => setView('login')}
                >
                  Volver a iniciar sesión
                </button>
              </p>
            )}
          </form>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            {view === 'login' && '¿Primera vez? Solicita una cotización y te enviaremos un correo para verificar tu cuenta.'}
          </p>
          <p className="mt-2 text-center text-sm">
            <Link to="/store" className="text-primary underline">Volver al store</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default StoreLogin;
